/**
 * The walkthrough's brain.
 *
 * Every step's status is derived from a snapshot of real state — chain reads,
 * wallet state, the launch journal — and nothing here guesses. A step is only
 * "done" because something was observed, which is why this file is pure: it
 * takes a snapshot in and returns steps out, so the derivation can be tested
 * without a browser, a wallet or a validator.
 *
 * It explains and checks. It never writes a form field and never signs. In a
 * tool where a wrong decimal is permanent, the person who types the value
 * should be the person who chose it.
 */

export type StepState =
  | "done" // observed to be true
  | "current" // the next thing to do
  | "blocked" // cannot be done until an earlier step is
  | "warn" // done, but something about it deserves a second look
  | "skipped"; // deliberately not applicable to this run

export interface GuideStep {
  id: string;
  phase: string;
  title: string;
  /** What to do, in the imperative. */
  action: string;
  /** Why it matters — the consequence of getting it wrong. */
  why: string;
  state: StepState;
  /** Present when the step's state was derived from something observed. */
  detail?: string;
  /** True when completing this step cannot be undone. */
  permanent?: boolean;
  /** Which console tab the work happens in. */
  tab?: string;
}

/** Everything the walkthrough is allowed to reason from. */
export interface GuideSnapshot {
  cluster: "devnet" | "mainnet-beta";
  networkLabel: string;
  isMainnet: boolean;

  /** null while the check is in flight. */
  rpcReachable: boolean | null;
  rpcVersion?: string;
  /**
   * Whether the CPMM program for the SELECTED cluster exists and is executable
   * at the SELECTED endpoint. This is the cluster-mismatch check: a devnet
   * config pointed at a mainnet node finds nothing at the devnet program id.
   */
  cpmmProgramPresent: boolean | null;

  walletConnected: boolean;
  walletLabel?: string;
  /** Lamports, or null when unknown. */
  balanceLamports: number | null;
  /** Lamports the current plan needs, or null when the plan is not known yet. */
  requiredLamports: number | null;

  /** Set by the Launch panel while it is mounted; null when it has not been opened. */
  launch: LaunchSnapshot | null;

  /** A token is loaded into the console (launched here, or loaded by mint). */
  hasToken: boolean;
  hasPool: boolean;
  /** Raw LP balance as a decimal string, or null when unknown. */
  lpBalanceRaw: string | null;
}

export interface LaunchSnapshot {
  openPool: boolean;
  /** Requirement label -> met. Mirrors the Launch panel's own checklist. */
  mintChecklist: { label: string; met: boolean }[];
  /** Blocking problems, already split by the panel. */
  mintProblems: string[];
  poolProblems: string[];
  /** The metadata URI was fetched and parsed, not merely typed. */
  uriProbe: { ok: boolean; detail: string } | null;
  /** Stage names recorded in the journal. */
  stagesDone: string[];
  /** The full stage list for the chosen scope. */
  stagesPlanned: string[];
  revokeMint: boolean;
  revokeFreeze: boolean;
  mutableMetadata: boolean;
  tierChosen: boolean;
  tierDescription?: string;
  openingPrice?: string;
  /** Lamports the current plan needs, as the Launch panel computes it. */
  requiredLamports: number | null;
  /** Unix seconds the pool opens for trading, or 0 for immediately. */
  openTimeUnix: number;
  /** The cluster's clock, which is the one the program compares against. */
  clusterNowSec: number | null;
}

const SOL = 1_000_000_000;

export function formatSol(lamports: number): string {
  return (lamports / SOL).toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * The steps, in the order a launch actually happens.
 *
 * The rule for `state`: "current" is given to exactly one step — the first that
 * is neither done nor skipped — so the panel always has one obvious next
 * action. Everything after it is "blocked", which is a statement about
 * ordering, not about the user.
 */
export function buildGuide(s: GuideSnapshot): GuideStep[] {
  const out: GuideStep[] = [];

  const push = (
    step: Omit<GuideStep, "state">,
    resolved: StepState | null,
    detail?: string
  ) => {
    out.push({ ...step, state: resolved ?? "blocked", detail });
  };

  const L = s.launch;

  // ---------------------------------------------------------------- prepare

  push(
    {
      id: "network",
      phase: "Before you start",
      title: "Pick the network you are rehearsing on",
      action:
        "Start on Localnet or Devnet. Do a complete launch there first, including a swap, before you ever select Mainnet-beta.",
      why: "Mainnet spends real SOL and nothing on it can be undone. A rehearsal costs nothing and finds the same mistakes.",
      tab: "top bar",
    },
    s.isMainnet ? "warn" : "done",
    s.isMainnet
      ? `${s.networkLabel} — every action from here spends real funds`
      : s.networkLabel
  );

  push(
    {
      id: "rpc",
      phase: "Before you start",
      title: "Confirm the endpoint is the network you think it is",
      action:
        "Check the RPC field. The console reads the CPMM program for the selected cluster straight off this endpoint.",
      why: "A devnet config pointed at a mainnet node — or the reverse — fails in confusing ways much later, usually after you have already paid for something.",
      tab: "top bar",
    },
    s.rpcReachable === null || s.cpmmProgramPresent === null
      ? "current"
      : s.rpcReachable === false
        ? "current"
        : s.cpmmProgramPresent === false
          ? "warn"
          : "done",
    s.rpcReachable === false
      ? "The endpoint did not answer."
      : s.cpmmProgramPresent === false
        ? `Reachable${s.rpcVersion ? ` (solana-core ${s.rpcVersion})` : ""}, but the ${s.cluster} CPMM program is not deployed there. Wrong network for this endpoint.`
        : s.rpcReachable
          ? `Reachable${s.rpcVersion ? ` (solana-core ${s.rpcVersion})` : ""}, ${s.cluster} CPMM program found`
          : undefined
  );

  push(
    {
      id: "wallet",
      phase: "Before you start",
      title: "Connect a wallet",
      action:
        "Use the Connect button. The console asks your extension for signatures; it never sees, stores or transmits a private key.",
      why: "This wallet pays every fee, receives the entire supply, and becomes the mint authority until you revoke it. Use one you control and can keep.",
      tab: "top bar",
    },
    s.walletConnected ? "done" : "current",
    s.walletConnected ? s.walletLabel : "Any page asking you to paste a seed phrase is stealing it, including one that looks like this."
  );

  const funded =
    s.balanceLamports !== null &&
    (s.requiredLamports === null
      ? s.balanceLamports > 0
      : s.balanceLamports >= s.requiredLamports);

  push(
    {
      id: "funding",
      phase: "Before you start",
      title: "Fund the wallet",
      action: s.isMainnet
        ? "Send SOL to the connected wallet."
        : "Use the devnet faucet at faucet.solana.com, or airdrop on a local validator.",
      why: "A run that dies between the mint and the metadata leaves a token that can never be named, because naming needs the mint authority you are about to revoke.",
      tab: "top bar",
    },
    !s.walletConnected
      ? "blocked"
      : s.balanceLamports === null
        ? "current"
        : funded
          ? "done"
          : "current",
    s.balanceLamports === null
      ? undefined
      : s.requiredLamports === null
        ? `${formatSol(s.balanceLamports)} SOL`
        : `${formatSol(s.balanceLamports)} SOL held, ${formatSol(s.requiredLamports)} SOL needed for this plan`
  );

  // ------------------------------------------------------------- decisions

  const checklistMet = L ? L.mintChecklist.every((c) => c.met) : false;
  const unmet = L ? L.mintChecklist.filter((c) => !c.met).map((c) => c.label) : [];

  push(
    {
      id: "identity",
      phase: "Decide before you type",
      title: "Name, symbol, decimals and supply",
      action:
        "Fill the four identity fields on the Launch tab. Decimals is 9 to match SOL or 6 to match USDC; the whole supply is minted to your wallet in one transaction.",
      why: "Decimals and supply are fixed at creation. Nine decimals with a supply of one billion is a number with eighteen zeros behind it — get it wrong and the only fix is a new token.",
      tab: "Launch",
    },
    !s.walletConnected
      ? "blocked"
      : !L
        ? "current"
        : checklistMet
          ? "done"
          : "current",
    !L
      ? "Open the Launch tab to see the live checklist."
      : unmet.length
        ? `Still missing: ${unmet.join(", ")}`
        : "All six requirements met"
  );

  push(
    {
      id: "metadata",
      phase: "Decide before you type",
      title: "Host the metadata JSON, then test the URI",
      action:
        "Upload a JSON file with name, symbol, description and image to somewhere permanent — IPFS or Arweave — and press Test URI so the console fetches it.",
      why: "This URI is what every wallet and explorer reads to show a name and a picture. If it 404s the token shows as a blank square, and if you also froze metadata, it shows as a blank square forever.",
      tab: "Launch",
    },
    !L
      ? s.walletConnected
        ? "blocked"
        : "blocked"
      : L.uriProbe === null
        ? checklistMet
          ? "current"
          : "blocked"
        : L.uriProbe.ok
          ? "done"
          : "warn",
    L?.uriProbe ? L.uriProbe.detail : "Not tested yet — typing a URI is not the same as it resolving."
  );

  push(
    {
      id: "authorities",
      phase: "Decide before you type",
      title: "Choose the authority policy",
      action:
        "Decide whether to revoke the mint authority, revoke the freeze authority, and whether metadata stays editable. None of the three is required to mint.",
      why: "All three are permanent the moment the transaction confirms. Revoking the mint authority is how you prove the supply is fixed; keeping it is how you retain the ability to inflate. Both are defensible. Doing it by accident is not.",
      permanent: true,
      tab: "Launch",
    },
    !L ? "blocked" : "done",
    L
      ? [
          L.revokeMint ? "mint authority: revoke" : "mint authority: keep",
          L.revokeFreeze ? "freeze authority: revoke" : "freeze authority: keep",
          L.mutableMetadata ? "metadata: stays editable" : "metadata: frozen",
        ].join(" · ")
      : undefined
  );

  // ------------------------------------------------------------------ pool

  const poolSkipped = L ? !L.openPool : false;

  push(
    {
      id: "liquidity",
      phase: "The pool",
      title: "Set the opening price",
      action:
        "Choose how many tokens and how much SOL go into the pool. The ratio between them is the opening price; nothing else sets it.",
      why: "This is the first price anyone can trade at, and the SOL you deposit is genuinely at risk — a thin pool moves violently on the first buy.",
      tab: "Launch",
    },
    poolSkipped
      ? "skipped"
      : !L
        ? "blocked"
        : L.poolProblems.length
          ? "current"
          : "done",
    poolSkipped
      ? "No pool in this run — the token will exist but will not be tradeable."
      : L?.openingPrice
        ? `Opening price ${L.openingPrice} SOL`
        : L?.poolProblems.length
          ? L.poolProblems[0]
          : undefined
  );

  push(
    {
      id: "feetier",
      phase: "The pool",
      title: "Pick a fee tier by its rate",
      action:
        "Choose from the dropdown, which is read live from the chain. Read the trade fee, not the position in the list.",
      why: "Index 1 is 0.30% on devnet and 1.00% on mainnet. Rehearse by index and launch by index and your pool charges more than three times what you tested.",
      tab: "Launch",
    },
    poolSkipped ? "skipped" : !L ? "blocked" : L.tierChosen ? "done" : "current",
    poolSkipped ? undefined : L?.tierDescription
  );

  // ------------------------------------------------------------------- run

  const started = L ? L.stagesDone.length > 0 : false;
  const finished =
    L !== null && L.stagesPlanned.length > 0 && L.stagesPlanned.every((x) => L.stagesDone.includes(x));

  push(
    {
      id: "schedule",
      phase: "The pool",
      title: "Decide when trading opens",
      action:
        "Leave it to open immediately, or set a date and time. The pool is created and verified now either way; only the trading window moves.",
      why: "The time is stored in the pool account and the cp-swap program refuses swaps until the cluster clock passes it — verified: swap_base_input.rs raises NotApproved (0x1770) before, and the same swap succeeds after. Nothing of yours has to be running, and the time cannot be changed once the pool exists.",
      permanent: true,
      tab: "Launch",
    },
    poolSkipped ? "skipped" : !L ? "blocked" : "done",
    poolSkipped
      ? undefined
      : L
        ? L.openTimeUnix === 0
          ? "Opens immediately on creation"
          : `Opens ${new Date(L.openTimeUnix * 1000).toISOString().replace(".000", "")} (unix ${L.openTimeUnix})`
        : undefined
  );

  push(
    {
      id: "run",
      phase: "The launch",
      title: "Run it, and read the verification table",
      action:
        "Press Launch and approve each wallet prompt. When it finishes, every row of the verification table must say PASS.",
      why: "Each value in that table was read back off the chain after it was written. A launch that reports success without them is a launch you are taking on trust.",
      permanent: true,
      tab: "Launch",
    },
    !L
      ? "blocked"
      : finished
        ? "done"
        : started
          ? "current"
          : checklistMet && (poolSkipped || L.poolProblems.length === 0)
            ? "current"
            : "blocked",
    L
      ? finished
        ? `All ${L.stagesPlanned.length} stages complete`
        : started
          ? `${L.stagesDone.length} of ${L.stagesPlanned.length} stages done — re-running resumes, it never creates a second mint`
          : `${L.stagesPlanned.length} stages: ${L.stagesPlanned.join(" → ")}`
      : undefined
  );

  // ----------------------------------------------------------------- after

  push(
    {
      id: "verify",
      phase: "After the launch",
      title: "Check it independently",
      action:
        "Open the Dashboard and confirm the mint, the authorities and the pool reserves. Then open the mint in an explorer and confirm the name and image render.",
      why: "The console reads the same chain an explorer does, but a metadata URI that resolves for you may not resolve for everyone. Look at it the way a holder will.",
      tab: "Dashboard",
    },
    !s.hasToken ? "blocked" : "current",
    s.hasToken ? (s.hasPool ? "Token and pool loaded" : "Token loaded, no pool") : undefined
  );

  push(
    {
      id: "lp",
      phase: "After the launch",
      title: "Decide what happens to the LP tokens",
      action:
        "Keep them, burn them, or lock them permanently with Burn & Earn. Then say publicly which one you did.",
      why: "Burning destroys your claim and every future fee. Burn & Earn also locks the liquidity forever but mints a Fee Key NFT that keeps the fees claimable — and whoever holds that NFT collects them. Both are irreversible, and neither is the time-limited lock most people mean by 'locked liquidity'.",
      permanent: true,
      tab: "Burn & LP",
    },
    !s.hasPool ? "skipped" : "current",
    s.lpBalanceRaw !== null ? `LP balance ${s.lpBalanceRaw} raw` : undefined
  );

  push(
    {
      id: "distribute",
      phase: "After the launch",
      title: "Distribute, if you are distributing",
      action:
        "Import a CSV of addresses and amounts. Every line is validated and the SOL cost of creating accounts for new holders is priced before you approve anything.",
      why: "You pay about 0.00204 SOL of rent for every recipient who has never held this token. For five hundred fresh wallets that is real money, and it is better known before than discovered halfway through.",
      tab: "Distribute",
    },
    !s.hasToken ? "blocked" : "skipped",
    "Optional."
  );

  // Exactly one "current": the first non-done, non-skipped step.
  let seenCurrent = false;
  return out.map((step) => {
    if (step.state === "done" || step.state === "skipped" || step.state === "warn") return step;
    if (!seenCurrent && step.state === "current") {
      seenCurrent = true;
      return step;
    }
    if (step.state === "current") return { ...step, state: "blocked" as StepState };
    return step;
  });
}

/** Progress across the steps that actually apply to this run. */
export function guideProgress(steps: GuideStep[]): { done: number; total: number } {
  const applicable = steps.filter((s) => s.state !== "skipped");
  return {
    done: applicable.filter((s) => s.state === "done" || s.state === "warn").length,
    total: applicable.length,
  };
}

/** The metadata file people most often get wrong, as a copyable starting point. */
export function metadataTemplate(name: string, symbol: string): string {
  return JSON.stringify(
    {
      name: name || "Your Token Name",
      symbol: symbol || "SYM",
      description: "One or two sentences. Shown by wallets and explorers.",
      image: "https://<permanent-host>/logo.png",
      external_url: "https://your-site.example",
    },
    null,
    2
  );
}
